import React, { useState, useEffect } from "react";
import { useAccessForm } from "../../hooks/useAccessForm";
import {
  ModalContainer,
  LoginHeader,
  LoginForm,
  RegistrationHeader,
  RegistrationForm,
  RegistrationSuccess,
  ErrorMessage,
} from "./accessComponents/AccessModalComponents";
import {
  countries,
  documentTypes,
  validatePhone,
  validateDocument,
} from "../../scripts/generalScript";

const AccessModal = ({ open, onClose, initialTab = "signin", redirectAfterLogin = true }) => {
  // Obtenemos la lógica del formulario desde el hook
  const {
    isRegistering,
    setIsRegistering,
    loading,
    error,
    showPassword,
    setShowPassword,
    signInData,
    setSignInData,
    handleSignIn,
    signUpData,
    setSignUpData,
    handleSignUp,
    passwordError,
    acceptTerms,
    setAcceptTerms,
    isActiveUser
    } = useAccessForm({ 
      open, 
      onClose, 
      initialTab,
      redirectAfterLogin
    });

  // Estado adicional para la vista de registro exitoso
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Estado para validaciones de contraseña
  const [validations, setValidations] = useState({
    hasUpperCase: false,
    hasNumber: false,
    minLength: false,
  });

  // En el componente, añadir estado para errores de validación
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    document_value: "",
  });

  // Prefijo telefónico
  const [phonePrefix, setPhonePrefix] = useState("+57");
  const [formDisabled, setFormDisabled] = useState(false);

  // Control de habilitación de campos
  const [fieldStatus, setFieldStatus] = useState({
    first_name: true,
    last_name: false,
    email: false,
    phone: false,
    nationality: false,
    document_type: false,
    document_value: false,
    password: false,
  });

  // Progreso del formulario
  const [formProgress, setFormProgress] = useState(0);

  // Función para verificar si hay datos parciales en el formulario
  const hasPartialFormData = () => {
    // Si hay al menos un campo con datos pero no todos los campos requeridos están completos
    const hasAnyData = Object.values(signUpData).some(value => 
      value !== null && value !== undefined && value !== "");
    
    // Verificar si el formulario está parcialmente completo pero no finalizado
    return hasAnyData && formProgress < 100;
  };

  // Función para confirmar la salida con datos parciales
  const confirmExit = () => {
    if (isRegistering && hasPartialFormData()) {
      return window.confirm("¿Estás seguro que deseas salir? Los datos ingresados no se guardarán.");
    }
    return true;
  };

  // Función para resetear completamente el formulario
  const resetFormData = () => {
    setSignUpData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      nationality: "",
      document_type: "",
      document_value: "",
      password: ""
    });
    setAcceptTerms(false);
    setPhonePrefix("+57");
    setValidationErrors({
      phone: "",
      document_value: ""
    });
  };

  // Función para detectar el proveedor de correo
  const getEmailProvider = (email) => {
    if (!email) return null;

    const domain = email.split("@")[1]?.toLowerCase();
    if (!domain) return null;

    if (domain.includes("gmail"))
      return { name: "Gmail", url: "https://mail.google.com" };
    if (domain.includes("outlook") || domain.includes("hotmail"))
      return { name: "Outlook", url: "https://outlook.live.com" };
    if (domain.includes("yahoo"))
      return { name: "Yahoo Mail", url: "https://mail.yahoo.com" };
    return null;
  };

  // Función modificada para manejar los cambios en los campos
  const handleChange = (field, value) => {
    // Validaciones específicas por campo
    if (field === "phone") {
      const validation = validatePhone(value);
      setValidationErrors((prev) => ({
        ...prev,
        phone: validation.errorMessage,
      }));
      value = validation.cleanValue;
    } else if (field === "document_value") {
      const validation = validateDocument(value, signUpData.document_type);
      setValidationErrors((prev) => ({
        ...prev,
        document_value: validation.errorMessage,
      }));
      value = validation.cleanValue;
    }

    // Actualizar datos según si es registro o login
    if (isRegistering) {
      setSignUpData((prev) => ({ ...prev, [field]: value }));
    } else {
      setSignInData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Función de registro modificada para manejar el éxito
  const handleSignUpWithSuccess = async (e) => {
    e?.preventDefault();

    // Evitar múltiples envíos
    if (loading) return;

    setFormDisabled(true);

    // Formato de teléfono con prefijo
    const formattedPhone = signUpData.phone
      ? `${phonePrefix}${signUpData.phone}`
      : "";

    try {
      // Llamar a la función original con el teléfono formateado
      const success = await handleSignUp(e, formattedPhone);

      // Si el registro fue exitoso, mostrar la vista de éxito
      if (success === true) {
        setRegistrationSuccess(true);
        console.log("Registro exitoso - Modal - Mostrar pantalla de éxito");
      } else {
        setFormDisabled(false);
      }

      return success;
    } catch (error) {
      console.error("Error en registro:", error);
      setFormDisabled(false);
      return false;
    }
  };

  // Validar la contraseña cuando cambie
  useEffect(() => {
    if (signUpData.password) {
      setValidations({
        hasUpperCase: /[A-Z]/.test(signUpData.password),
        hasNumber: /[0-9]/.test(signUpData.password),
        minLength: signUpData.password.length >= 8,
      });
    }
  }, [signUpData.password]);

  // Actualizar habilitación de campos y progreso del formulario
  useEffect(() => {
    // Actualizar habilitación de campos
    const newFieldStatus = {
      first_name: true,
      last_name: !!signUpData.first_name,
      email: !!signUpData.first_name && !!signUpData.last_name,
      phone:
        !!signUpData.first_name && !!signUpData.last_name && !!signUpData.email,
      nationality:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone,
      document_type:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality,
      document_value:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality &&
        !!signUpData.document_type,
      password:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality &&
        !!signUpData.document_type &&
        !!signUpData.document_value,
    };

    setFieldStatus(newFieldStatus);

    // Calcular progreso del formulario
    const totalFields = 9; // 8 campos + términos
    let completedFields = 0;

    if (signUpData.first_name) completedFields++;
    if (signUpData.last_name) completedFields++;
    if (signUpData.email) completedFields++;
    if (signUpData.phone) completedFields++;
    if (signUpData.nationality) completedFields++;
    if (signUpData.document_type) completedFields++;
    if (signUpData.document_value) completedFields++;
    if (signUpData.password) completedFields++;
    if (acceptTerms) completedFields++;

    setFormProgress((completedFields / totalFields) * 100);
  }, [signUpData, acceptTerms]);

  // Agregar evento beforeunload para confirmar salida si hay datos
  useEffect(() => {
    // Sólo agregar el listener si estamos en modo registro y hay datos parciales
    if (isRegistering && hasPartialFormData()) {
      const handleBeforeUnload = (e) => {
        // Mensaje estándar para confirmar salida (el texto exacto lo decide el navegador)
        e.preventDefault();
        e.returnValue = '';
        return '';
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isRegistering, signUpData, formProgress]);

  // Manejar el retorno a la pantalla de login desde la pantalla de éxito
  const handleReturnToLogin = () => {
    setIsRegistering(false);
    setRegistrationSuccess(false);
    resetFormData();
  };

  // Cambiar a registro con confirmación si hay datos incompletos
  const handleSwitchToRegister = () => {
    if (confirmExit()) {
      setIsRegistering(true);
      resetFormData();
    }
  };

  // Handle back button from registration form
  const handleBackFromRegistration = () => {
    if (confirmExit()) {
      setIsRegistering(false);
      resetFormData();
    }
  };

  // Reiniciar estados cuando se cierra el modal
  const handleClose = () => {
    if (confirmExit()) {
      setRegistrationSuccess(false);
      setFormDisabled(false);
      resetFormData();
      onClose();
    }
  };

  // Si el modal no está abierto, no renderizar nada
  
  useEffect(() => {
    // Si el usuario inicia sesión correctamente, cerrar modal automáticamente
    if (isActiveUser) {
      onClose();
    }
  }, [isActiveUser, onClose]);
  
  if (!open) return null;

  return (
    <ModalContainer onClose={handleClose}>
      {/* Contenido según el estado actual */}
      {!isRegistering && <LoginHeader />}

      {isRegistering ? (
        registrationSuccess ? (
          /* Vista de registro exitoso */
          <RegistrationSuccess
            email={signUpData.email}
            setIsRegistering={handleReturnToLogin}
            emailProvider={getEmailProvider(signUpData.email)}
          />
        ) : (
          /* Vista de formulario de registro */
          <>
            <RegistrationHeader setIsRegistering={handleBackFromRegistration} />
            <div className="overflow-auto">
              <RegistrationForm
                signUpData={signUpData}
                handleChange={handleChange}
                handleSignUp={handleSignUpWithSuccess}
                fieldStatus={fieldStatus}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                validations={validations}
                passwordError={passwordError}
                phonePrefix={phonePrefix}
                setPhonePrefix={setPhonePrefix}
                acceptTerms={acceptTerms}
                setAcceptTerms={setAcceptTerms}
                formProgress={formProgress}
                loading={loading}
                documentTypes={documentTypes}
                countries={countries}
                formDisabled={formDisabled}
                validationErrors={validationErrors}
                isModal={true}
              />
            </div>
          </>
        )
      ) : (
        /* Vista de inicio de sesión */
        <LoginForm
          signInData={signInData}
          handleChange={handleChange}
          handleSignIn={handleSignIn}
          loading={loading}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          setIsRegistering={handleSwitchToRegister}
        />
      )}

      {/* Mensaje de error si existe */}
      {error && <ErrorMessage error={error} />}
    </ModalContainer>
  );
};

export default AccessModal;